/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useBTSChatContext } from './BTSChatContext';
import { useChat } from '@jaybel/chat-lib';
import { IoSend } from 'react-icons/io5';

const ChatPanel = () => {
  const { selectedClass, refreshList, updateLastMessageLocally } = useBTSChatContext();
  const [text, setText] = useState('');
  const messagesEndRef = useRef(null);

  // Call the hook only if selectedClass is active
  const chatProps = useChat({
    quoteCode: selectedClass ? String(selectedClass.schoolCode) : '',
    classId: selectedClass ? String(selectedClass.classId) : '',
  });

  const {
    messages = [],
    isLoadingHistory,
    isSending,
    sendMessage,
    currentUser,
    isConnected,
  } = selectedClass ? chatProps : {};

  // Scroll to bottom helper
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Scroll when messages change
  useEffect(() => {
    if (selectedClass && messages.length > 0) {
      scrollToBottom('auto');
    }
  }, [messages, selectedClass]);

  const lastClassIdRef = useRef(null);
  const lastMessageRef = useRef(null);

  // Refresh left panel unread status when room is selected or new incoming messages arrive
  useEffect(() => {
    if (selectedClass) {
      // 1. Refresh list when switching to a different room
      if (selectedClass.classId !== lastClassIdRef.current) {
        refreshList();
        lastClassIdRef.current = selectedClass.classId;
      }

      // 2. Refresh list when a new incoming message is received
      if (messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        const isMine =
          lastMsg.senderRole === 'REP' ||
          lastMsg.senderRole === (currentUser?.role || 'REP') ||
          lastMsg.senderId === currentUser?.id;

        if (!isMine && lastMsg.id !== lastMessageRef.current?.id) {
          refreshList();
        }
        lastMessageRef.current = lastMsg;
      }
    } else {
      lastClassIdRef.current = null;
    }
  }, [selectedClass, messages, currentUser, refreshList]);

  if (!selectedClass) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-500 p-8 h-full">
        <svg
          className="w-16 h-16 text-gray-300 mb-4 animate-pulse"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <h3 className="text-lg font-bold text-gray-700 mb-1">Select a Class</h3>
        <p className="text-sm text-gray-400 text-center max-w-xs">
          Choose a school class from the left list to view unread messages and start typing.
        </p>
      </div>
    );
  }

  const handleSend = async () => {
    if (!text.trim() || isSending) return;
    const msgText = text;
    setText('');
    try {
      await sendMessage(msgText);
      updateLastMessageLocally(selectedClass.schoolCode, selectedClass.classId, msgText);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Helper to format date labels (e.g. Today, Yesterday, Date)
  const getMessageDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
    }
  };

  // Helper to format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Group messages by date to render dividers
  const renderMessageList = () => {
    const listElements = [];
    let lastDateLabel = null;

    messages.forEach((msg, idx) => {
      if (msg.senderId === 'SYSTEM' && msg.content === 'PING') return;

      const currentDateLabel = getMessageDateLabel(msg.createdAt);
      if (currentDateLabel !== lastDateLabel) {
        listElements.push(
          <div key={`date-${currentDateLabel}-${idx}`} className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
              {currentDateLabel}
            </span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
        );
        lastDateLabel = currentDateLabel;
      }

      // Check if message is mine (outgoing)
      const isMine =
        msg.senderRole === 'REP' ||
        msg.senderRole === (currentUser?.role || 'REP') ||
        msg.senderId === currentUser?.id;

      listElements.push(
        <div key={msg.id || idx} className={`flex flex-col mb-4 ${isMine ? 'items-end' : 'items-start'}`}>
          {/* User Name label above incoming bubble */}
          {!isMine && (
            <span className="text-xs font-semibold text-gray-500 mb-1 ml-1">
              User Name
            </span>
          )}

          <div
            className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm whitespace-pre-wrap break-words ${
              isMine
                ? 'bg-gray-100 text-gray-800 rounded-tr-none border border-gray-200'
                : 'bg-[#E0F2FE] text-gray-800 rounded-tl-none border border-[#BAE6FD]'
            }`}
          >
            {msg.content ? (
              msg.content.split('\n').map((line, index, arr) => (
                <React.Fragment key={index}>
                  {line}
                  {index < arr.length - 1 && <br />}
                </React.Fragment>
              ))
            ) : null}
          </div>

          {/* Info row below bubble */}
          <span className="text-[10px] text-gray-400 mt-1 mx-1">
            {isMine ? (
              `${currentUser?.name || 'Sales Rep'} · ${formatTime(msg.createdAt)}`
            ) : (
              `${msg.senderId} · ${formatTime(msg.createdAt)}`
            )}
          </span>
        </div>
      );
    });

    return listElements;
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      {/* Header */}
      <div className="px-6 py-3 bg-[#E5E7EB] border-b border-gray-200 flex items-center justify-between min-h-[64px]">
        <div className="flex flex-col justify-center">
          <h2 className="text-sm font-bold text-gray-800 leading-tight">
            {selectedClass.className}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {selectedClass.schoolName}
          </p>
        </div>

        {/* Connection status indicator */}
        <div className="flex items-center gap-1.5 px-3 py-1 bg-white/60 backdrop-blur-sm rounded-full border border-gray-200 shadow-sm">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300 ${
              isConnected ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.8)]' : 'bg-gray-400'
            }`}
          />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-600">
            {isConnected ? 'Online' : 'Connecting...'}
          </span>
        </div>
      </div>

      {/* Message Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-6 h-6 border-2 border-[#E8A020] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <span className="text-sm font-medium">No messages yet. Say hello! 👋</span>
          </div>
        ) : (
          <div>
            {renderMessageList()}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Row */}
      <div className="p-4 border-t border-gray-100 bg-white">
        <div className="flex items-end gap-3">
          <textarea
            placeholder="Type a message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            rows={Math.min(8, Math.max(2, text.split('\n').length))}
            className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#E8A020] focus:border-[#E8A020] placeholder-gray-400 resize-none overflow-y-auto"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || isSending}
            className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[rgb(237,28,36)] rounded-full hover:bg-[rgb(217,26,34)] text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(237,28,36)] mb-0.5"
          >
            <IoSend size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
